import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  HOSTING_TYPES,
  HostingType,
} from 'network/httpClients/RepositoryClient/api.schema';
import { QUERY_PARAMS } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { Image } from '../../Image';
import {
  FIELDS,
  FORM_IDS,
  ORGANIZATION_URL_PLACEHOLDER_SAAS,
  ORGANIZATION_URL_PLACEHOLDER_SELF_HOSTED,
} from '../consts';
import { useGithubInstallation } from '../githubInstallationContext';
import { getOrganizationDetailsFormSchema } from '../schemas';
import { OrganizationDetailsFormData } from '../types';
import { isValidHostingType, prependHttpsIfNeeded } from '../utils';
import organizationDetailsScreenshot from '/images/github-installation/organization-details.svg';

export const OrganizationDetails = () => {
  const { goNext, setEarliestStepAvailable } = useMultiStepDialog();
  const { organizationUrl, setOrganizationUrl } = useGithubInstallation();
  const [searchParams] = useSearchParams();

  const hostingTypeParam = searchParams.get(QUERY_PARAMS.HOSTING_TYPE);
  const hostingType: HostingType = isValidHostingType(hostingTypeParam)
    ? hostingTypeParam
    : HOSTING_TYPES.SELF_HOSTED;

  const methods = useForm<OrganizationDetailsFormData>({
    resolver: zodResolver(getOrganizationDetailsFormSchema(hostingType)),
    defaultValues: {
      [FIELDS.ORGANIZATION_URL]: organizationUrl,
    },
  });

  const spawnForm = spawn(
    'dialogs.github-installation.organization-details-form',
  );

  const organizationUrlPlaceholder =
    hostingType === HOSTING_TYPES.SELF_HOSTED
      ? ORGANIZATION_URL_PLACEHOLDER_SELF_HOSTED
      : ORGANIZATION_URL_PLACEHOLDER_SAAS;

  const formClassName = cn(
    "[&_[data-slot='form-label']]:font-mono",
    "[&_[data-slot='form-label']]:text-xs",
    "[&_[data-slot='form-label']]:leading-[140%]",
    "[&_[data-slot='form-description']]:text-text-general-neutral-mild",
  );

  useEffect(() => {
    setEarliestStepAvailable(0);
  }, []);

  const onSubmit = (data: Record<string, string>) => {
    setOrganizationUrl(prependHttpsIfNeeded(data[FIELDS.ORGANIZATION_URL]));

    goNext();
  };

  return (
    <Step title={spawnForm('title')} description={spawnForm('description')}>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.ORGANIZATION_DETAILS } as Record<string, string>)}
      >
        <Form.Input
          name={FIELDS.ORGANIZATION_URL}
          label={spawnForm('organization-url.label')}
          placeholder={organizationUrlPlaceholder}
        />
        <Image
          source={organizationDetailsScreenshot}
          alt={spawnForm('organization-url.label')}
        />
      </Form>
    </Step>
  );
};
